import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, SortItem } from "namirasoft-core";
import { JobInputRow, JobPlatform, JobRow, JobStatus, ReasonRow } from "namirasoft-job-arranger";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSBoxURL, NSColumn, NSRow } from "namirasoft-site-react";
import React, { createRef } from "react";
import { JobArrangerEntities } from "../JobArrangerEntities";
import { JobArrangerLayout } from '../JobArrangerLayout';
import { JobArrangerProps } from "../JobArrangerProps";
import { JobStatusPanel } from "../components/JobStatusPanel";
import { JobStatusHistory } from "../components/JobStatusHistory";

export interface JobEditPageProps extends NSARouterMakerResult, JobArrangerProps
{
    isEdit: boolean;
}

export interface JobEditPageState
{
    platform: JobPlatform | null;
    row: JobRow | null;
}

export default class JobEditPage extends React.Component<JobEditPageProps, JobEditPageState>
{
    JobStatusPanel_Ref = createRef<JobStatusPanel>();
    JobStatusHistory_Ref = createRef<JobStatusHistory>();

    NSBoxEnum_Status = createRef<NSBoxEnum<JobStatus>>();
    NSBoxString_Sid = createRef<NSBoxString>();
    NSBoxEntity_Platform = createRef<NSBoxEnum<JobPlatform>>();
    NSBoxEntity_Reason = createRef<NSBoxEntity<ReasonRow>>();
    NSBoxURL_Url = createRef<NSBoxURL>();
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxString_Company = createRef<NSBoxString>();
    NSBoxString_Location = createRef<NSBoxString>();
    NSBoxString_Remote = createRef<NSBoxString>();
    NSBoxString_Min = createRef<NSBoxString>();
    NSBoxString_Max = createRef<NSBoxString>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: JobEditPageProps)
    {
        super(props);
        this.state = { platform: null, row: null };
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <JobArrangerLayout<JobRow, JobInputRow>
                {...this.props}
                pages={{
                    entity: JobArrangerEntities.job(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let sid: string | null = this.NSBoxString_Sid.current?.getValue() ?? null;
                                        let platform: JobPlatform = this.NSBoxEntity_Platform.current?.getValueEnumOne(JobPlatform, JobPlatform.None) ?? JobPlatform.None;
                                        let reason_id: (string | null) = this.NSBoxEntity_Reason.current?.getValueOne() ?? null;
                                        let url: string = this.NSBoxURL_Url.current?.getValue() ?? "";
                                        let name: string = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let company: string = this.NSBoxString_Company.current?.getValue() ?? "";
                                        let location: string = this.NSBoxString_Location.current?.getValue() ?? "";
                                        let remote: string = this.NSBoxString_Remote.current?.getValue() ?? "";
                                        let min: (string | null) = this.NSBoxString_Min.current?.getValue() ?? null;
                                        let max: (string | null) = this.NSBoxString_Max.current?.getValue() ?? null;
                                        let status: JobStatus = this.NSBoxEnum_Status.current?.getValueEnumOne(JobStatus, JobStatus.WhiteList) ?? JobStatus.WhiteList;
                                        let description: (string | null) = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        return {
                                            workspace_id: "",
                                            sid,
                                            reason_id,
                                            platform,
                                            url,
                                            name,
                                            company,
                                            location,
                                            remote,
                                            min,
                                            max,
                                            status,
                                            description,
                                            job_category: [],
                                            job_field: [],
                                            job_tag: [],
                                        };
                                    },
                                    setRow: (row) =>
                                    {
                                        this.setState(prev => ({ ...prev, row }));
                                        this.setState(() => ({ platform: row.platform }), () =>
                                        {
                                            this.NSBoxEntity_Platform.current?.setValue(row.platform);
                                            this.NSBoxString_Sid.current?.setValue(row.sid);
                                        })
                                        this.NSBoxEntity_Reason.current?.setValue(row.reason_id);
                                        this.NSBoxURL_Url.current?.setValue(row.url);
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxString_Company.current?.setValue(row.company);
                                        this.NSBoxString_Location.current?.setValue(row.location);
                                        this.NSBoxString_Remote.current?.setValue(row.remote ?? "");
                                        this.NSBoxString_Min.current?.setValue(row.min ?? "");
                                        this.NSBoxString_Max.current?.setValue(row.max ?? "");
                                        this.NSBoxEnum_Status.current?.setValue(row.status);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");
                                    }
                                },
                            },
                            getChildrenAfter: (_id, row) =>
                            {
                                if (!row)
                                    return null;

                                return (
                                    <>
                                        <JobStatusPanel
                                            {...this.props}
                                            ref={this.JobStatusPanel_Ref}
                                            job={row}
                                            onChange={() =>
                                            {
                                                this.JobStatusHistory_Ref.current?.load();
                                            }}
                                        />
                                        <JobStatusHistory
                                            {...this.props}
                                            ref={this.JobStatusHistory_Ref}
                                            job={row}
                                            onChange={() => this.JobStatusPanel_Ref.current?.reloadJob()}
                                        />
                                    </>
                                )
                            }
                        }
                    }
                }}
            >
                <>
                    <NSColumn>
                        <NSRow style={{ justifyContent: "flex-start" }}>
                            <NSBoxEnum<JobPlatform>
                                ref={this.NSBoxEntity_Platform}
                                required
                                title="Platform"
                                getEnumObject={() => JobPlatform}
                                multiple={false}
                                onChanged={() =>
                                {
                                    let platform = this.NSBoxEntity_Platform.current?.getValueOne() as JobPlatform;
                                    this.setState(prev => ({ ...prev, platform }));
                                }}
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-platform" }}
                            />
                            {
                                (this.state.platform && this.state.platform !== JobPlatform.None) &&
                                <NSBoxString
                                    ref={this.NSBoxString_Sid}
                                    required
                                    title="SID"
                                    info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-sid" }}
                                />
                            }
                            <NSBoxString
                                ref={this.NSBoxString_Name}
                                required
                                title="Name"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-name" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxString
                                ref={this.NSBoxString_Company}
                                required
                                title="Company"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-company" }}
                            />
                            <NSBoxURL
                                ref={this.NSBoxURL_Url}
                                required
                                title="URL"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-url" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxString
                                ref={this.NSBoxString_Location}
                                required
                                title="Location"
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-location" }}
                            />
                            <NSBoxString
                                ref={this.NSBoxString_Remote}
                                required={false}
                                title="Remote"
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-remote" }}
                            />
                            <NSBoxString
                                ref={this.NSBoxString_Min}
                                required={false}
                                title="Min"
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-min" }}
                            />
                            <NSBoxString
                                ref={this.NSBoxString_Max}
                                required={false}
                                title="Max"
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-max" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxEnum<JobStatus>
                                ref={this.NSBoxEnum_Status}
                                required
                                title="Status"
                                getEnumObject={() => JobStatus}
                                multiple={false}
                                style={{ width: NSBox.width.double }}
                                onChanged={() =>
                                {
                                    let box = this.NSBoxEntity_Reason.current;
                                    box?.setValueOne(null, box.reload);
                                }}
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-status" }}
                            />
                            <NSBoxEntity<ReasonRow>
                                ref={this.NSBoxEntity_Reason}
                                required={false}
                                title="Reason"
                                multiple={false}
                                table_name="reason"
                                info={{ link: "https://namirasoft.com/job-arranger/console-guide/job/#field-reason" }}
                                list={async (filters, page, size, sort) =>
                                {
                                    if (!filters)
                                        filters = [];

                                    filters.push(new FilterItem(
                                        namirasoft.job_arranger.tables.reason,
                                        namirasoft.job_arranger.tables.reason.columns.status,
                                        false,
                                        FilterItemOperator.all.equals,
                                        this.NSBoxEnum_Status.current?.getValueOne(false) ?? ""
                                    ));

                                    return await this.props.servers.job_arranger.reason.List(filters, page, size, sort, null) ?? { count: 0, rows: [] };
                                }}
                                style={{ width: NSBox.width.double }}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() =>
                                {
                                    let namirasoft = new NamirasoftMap();
                                    let table = namirasoft.job_arranger.tables.reason;
                                    let column = table.columns.name;
                                    return new SortItem(table, column, true);
                                }}
                            />
                        </NSRow>
                        <NSBoxTextArea
                            ref={this.NSBoxTextArea_Description}
                            title="Description"
                            required={false}
                            style={{ width: "100%" }}
                        />
                    </NSColumn>
                </>
            </JobArrangerLayout >
        );
    }
}